<template>
  <div class="subheader py-2 py-lg-4 subheader-solid" v-bind:class="subheaderClasses" id="kt_subheader"
    style="background-color: none !important;">

    <div class="d-flex align-items-center justify-content-between flex-wrap flex-sm-nowrap"
      v-bind:class="{ 'container-fluid': widthFluid, container: !widthFluid }">
      <div class="d-flex align-items-center flex-wrap mr-1">
        <ul class="breadcrumb breadcrumb-transparent breadcrumb-dot font-weight-bold p-0 my-1"
          style="font-family: Inter, ui-sans-serif, sans-serif !important;">
          <template v-for="(breadcrumb, i) in breadcrumbs">
            <li class="breadcrumb-item" :key="`${i}-${breadcrumb.title}`">
              <router-link v-if="breadcrumb.route" :to="{ name: breadcrumb.route }"
                class="text-primary title font-poppins">
                {{ breadcrumb.title }}
              </router-link>
              <span class="text-primary title  font-poppins" v-else>
                {{ breadcrumb.title }}
              </span>
              <span v-if="i < breadcrumbs.length - 1" class="small-icon">
                /
              </span>
            </li>
          </template>
        </ul>
      </div>
    </div>

    <!-- Existing system chip: missed payment / grace / expired -->
    <div
      v-if="showPaymentWarningChip"
      class="subscription-warning-bar"
      :class="{ 'is-grace': isInGracePeriod, 'is-expired': isExpiredSubscription }"
      v-bind:class="{ 'container-fluid': widthFluid, container: !widthFluid }"
    >
      <div class="subscription-warning">
        <i class="fas fa-exclamation-circle mr-2"></i>
        <span class="font-weight-bold font-poppins mr-2">
          {{ getSubscriptionMessage }}
        </span>
        <button class="btn btn-sm btn-light font-poppins ml-2" type="button" @click="redirectToBilling">
          <i class="fas fa-sync-alt mr-1"></i>
          {{ paymentWarningCta }}
        </button>
      </div>
    </div>

    <!-- Soft chip when complimentary is ending soon (still active) -->
    <div
      v-else-if="showComplimentaryWarningChip"
      class="subscription-warning-bar is-info"
      v-bind:class="{ 'container-fluid': widthFluid, container: !widthFluid }"
    >
      <div class="subscription-warning info">
        <i class="fas fa-gift mr-2"></i>
        <span class="font-weight-bold font-poppins mr-2">
          {{ complimentaryWarningMessage }}
        </span>
        <button class="btn btn-sm btn-light font-poppins ml-2" type="button" @click="redirectToBilling">
          <i class="fas fa-arrow-up mr-1"></i>
          Subscribe
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import { authSet } from "@/core/services/auth-storage";

export default {
  name: "KTSubheader",
  props: {
    breadcrumbs: Array,
    title: String
  },
  data() {
    return {
      subscriptionTick: 0,
      refreshingSubscription: false,
    };
  },
  computed: {
    ...mapGetters(["layoutConfig"]),

    getSubscription() {
      // subscriptionTick forces recompute after quiet refresh from API
      void this.subscriptionTick;
      return this.$getSubscription();
    },

    hasActiveSubscription() {
      const sub = this.getSubscription;

      // If no subscription exists, don't show expiration message
      if (!sub) {
        return true;
      }

      const planName = sub.plan?.name || "Free";

      // Free tier: no payment warning chip
      if (planName === "Free") {
        return true;
      }

      // Complimentary Start is still "active" access — soft chip handles ending-soon
      if (sub.is_complimentary && sub.status === "active") {
        return true;
      }

      // Paid plans: only fully active hides the payment chip
      // grace_period / expired / canceled still show the chip
      return sub.status === "active";
    },

    isInGracePeriod() {
      const sub = this.getSubscription;
      if (!sub) return false;
      const planName = sub.plan?.name || "Free";
      if (planName === "Free") return false;
      return sub.status === "grace_period";
    },

    isExpiredSubscription() {
      const sub = this.getSubscription;
      if (!sub) return false;
      return sub.status === "expired" || sub.status === "canceled";
    },

    showPaymentWarningChip() {
      return !this.hasActiveSubscription && !this.isAdminUser;
    },

    showComplimentaryWarningChip() {
      if (this.isAdminUser || this.showPaymentWarningChip) return false;
      const sub = this.getSubscription;
      if (!sub || !sub.is_complimentary) return false;
      const planName = sub.plan?.name || "Free";
      if (planName === "Free") return false;
      if (sub.status !== "active") return false;

      const endRaw = sub.complimentary_ends_at || sub.end_date;
      if (!endRaw) return true;
      const end = new Date(endRaw);
      if (Number.isNaN(end.getTime())) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      const days = Math.round((end - today) / 86400000);
      return days <= 14;
    },

    complimentaryWarningMessage() {
      const sub = this.getSubscription;
      const endRaw = sub?.complimentary_ends_at || sub?.end_date;
      if (!endRaw) {
        return "Your complimentary Start plan is ending soon. Subscribe to keep access.";
      }
      return `Your complimentary Start plan ends on ${this.formatDate(endRaw)}. Subscribe to keep access.`;
    },

    paymentWarningCta() {
      if (this.isInGracePeriod) return "Update payment & renew";
      return "Renew Now";
    },

    getSubscriptionMessage() {
      const sub = this.getSubscription;

      if (!sub) {
        return "Your subscription needs renewal";
      }
      if (sub.status === "expired") {
        return "Your subscription has expired and your site may be deactivated. Renew to restore access.";
      }
      // Missed payment — grace window (deactivate after grace, not Free downgrade)
      if (sub.status === "grace_period") {
        if (sub.grace_period_start) {
          const gracePeriodEnd = new Date(sub.grace_period_start);
          gracePeriodEnd.setDate(gracePeriodEnd.getDate() + 5);
          return `Payment failed. Update your card and renew before ${this.formatDate(gracePeriodEnd.toISOString().split("T")[0])} to avoid deactivation.`;
        }
        return "Payment failed. Renew soon to avoid deactivation.";
      }

      if (sub.status === "canceled") {
        return "Your subscription was canceled. Renew to restore access.";
      }

      if (sub.is_complimentary) {
        return this.complimentaryWarningMessage;
      }

      if (sub.end_date) {
        const endDate = new Date(sub.end_date);
        const today = new Date();
        if (endDate < today) {
          return "Your subscription has expired. Renew to restore access.";
        }
        return `Your subscription needs attention. Period ends on ${this.formatDate(sub.end_date)}.`;
      }

      return "Your subscription needs renewal";
    },

    isAdminUser() {
      const currentRoute = this.$route.path;
      return currentRoute.includes("/admin/") || currentRoute.includes("/admin");
    },

    widthFluid() {
      return this.layoutConfig("subheader.width") === "fluid";
    },

    subheaderClasses() {
      const classes = [];
      const style = this.layoutConfig("subheader.style");
      if (style) {
        classes.push(style);

        if (this.layoutConfig("subheader.fixed")) {
          classes.push("border-top");
        }
      }
      return classes.join(" ");
    },
  },
  watch: {
    "$route.fullPath"() {
      this.refreshSubscriptionQuietly();
    },
  },
  mounted() {
    this.refreshSubscriptionQuietly();
  },
  methods: {
    redirectToBilling() {
      this.$redirectToBilling();
    },

    formatDate(date) {
      if (!date) return "N/A";

      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    /**
     * Quietly refresh subscription so grace/missed-payment chip stays accurate
     * after cron renewals (without requiring re-login). Failures are ignored.
     */
    async refreshSubscriptionQuietly() {
      if (this.isAdminUser || this.refreshingSubscription) return;
      // Only refresh when a subscription is already cached — avoids getAllPlans
      // auto-creating a Free plan for users/staff who have none.
      const existing = this.$getSubscription();
      if (!existing || !existing.id) return;

      this.refreshingSubscription = true;
      try {
        const { data } = await ApiService.get("business/settings", "plans");
        if (data && data.subscription) {
          authSet("subscription", JSON.stringify(data.subscription));
          this.subscriptionTick += 1;
        }
      } catch (e) {
        // Staff without billing access / network — keep cached subscription
      } finally {
        this.refreshingSubscription = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-v-dropdown {
  &.dropdown-toggle {
    padding: 0;

    &:hover {
      text-decoration: none;
    }

    &.dropdown-toggle-no-caret {
      &:after {
        content: none;
      }
    }
  }

  &.dropdown-menu {
    margin: 0;
    padding: 0;
    outline: none;

    .b-dropdown-text {
      padding: 0;
    }
  }
}

.subheader {
  background-color: #F6FBFF !important;
}

.small-icon {
  font-size: 15px !important;
  font-weight: 900 !important;
}

.breadcrumb.breadcrumb-dot .breadcrumb-item:after {
  content: none !important
}

.title {
  font-weight: 500 !important;
  font-family: Inter, ui-sans-serif, sans-serif !important;
  letter-spacing: 0.2px !important;
}

.breadcrumb {
  background: transparent !important;
  padding: 1rem 0 !important;
  margin: 0 !important;
  font-family: Inter, ui-sans-serif, sans-serif !important;
  -webkit-font-smoothing: antialiased !important;
  -moz-osx-font-smoothing: grayscale !important;
}

.breadcrumb-item {
  font-family: Inter, ui-sans-serif, sans-serif !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  font-size: 1rem !important;
  display: flex !important;
  align-items: center !important;
  text-rendering: optimizeLegibility !important;
}

.breadcrumb-item a {
  color: #5D5BA0 !important;
  text-decoration: none !important;
  transition: all 0.3s ease !important;
  font-weight: 600 !important;
  padding: 0.5rem 0 !important;
  position: relative !important;
  border-bottom: 2px solid transparent !important;
}

.breadcrumb-item a:hover {
  color: #4a4a8a !important;
  border-bottom-color: #5D5BA0 !important;
  transform: translateY(-1px) !important;
}

.breadcrumb-item span {
  color: #5D5BA0 !important;
  font-weight: 700 !important;
  padding: 0.5rem 0 !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
}

.small-icon {
  font-size: 14px !important;
  color: #cbd5e0 !important;
  margin: 0 1rem !important;
  font-weight: 600 !important;
  opacity: 0.7 !important;
}

.subscription-warning-bar {
  padding-top: 0 !important;
  padding-bottom: 0.5rem !important;
}

.subscription-warning {
  display: inline-flex !important;
  align-items: center !important;
  background: #fff8e1 !important;
  border-left: 4px solid #ffc107 !important;
  padding: 0.45rem 1.25rem !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  transition: all 0.3s ease !important;
  animation: slideInRight 0.5s ease !important;
  max-width: 960px !important;
  flex-wrap: wrap !important;
  border: 1px solid #ffe082 !important;
}

.subscription-warning-bar.is-grace .subscription-warning {
  background: #fff7ed !important;
  border-color: #fdba74 !important;
  border-left-color: #ea580c !important;
}

.subscription-warning-bar.is-expired .subscription-warning {
  background: #fef2f2 !important;
  border-color: #fecaca !important;
  border-left-color: #dc2626 !important;
}

.subscription-warning-bar.is-info .subscription-warning,
.subscription-warning.info {
  background: #eff6ff !important;
  border-color: #93c5fd !important;
  border-left-color: #2563eb !important;
}

.subscription-warning:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
}

.subscription-warning i {
  font-size: 1.1rem !important;
  color: #f57c00 !important;
}

.subscription-warning-bar.is-grace .subscription-warning i {
  color: #ea580c !important;
}

.subscription-warning-bar.is-expired .subscription-warning i {
  color: #dc2626 !important;
}

.subscription-warning.info i {
  color: #2563eb !important;
}

.subscription-warning span {
  font-size: 0.9rem !important;
  line-height: 1.4 !important;
  color: #424242 !important;
}

.subscription-warning .btn-light {
  font-weight: 600 !important;
  padding: 0.35rem 1rem !important;
  border-radius: 6px !important;
  transition: all 0.3s ease !important;
  white-space: nowrap !important;
  background: #fff !important;
  color: #f57c00 !important;
  border: 1px solid #f57c00 !important;
}

.subscription-warning-bar.is-grace .subscription-warning .btn-light {
  color: #ea580c !important;
  border-color: #ea580c !important;
}

.subscription-warning-bar.is-expired .subscription-warning .btn-light {
  color: #dc2626 !important;
  border-color: #dc2626 !important;
}

.subscription-warning.info .btn-light {
  color: #2563eb !important;
  border-color: #2563eb !important;
}

.subscription-warning .btn-light:hover {
  background: #f57c00 !important;
  color: #fff !important;
  border-color: #f57c00 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 2px 8px rgba(245, 124, 0, 0.3) !important;
}

.subscription-warning-bar.is-grace .subscription-warning .btn-light:hover {
  background: #ea580c !important;
  border-color: #ea580c !important;
}

.subscription-warning-bar.is-expired .subscription-warning .btn-light:hover {
  background: #dc2626 !important;
  border-color: #dc2626 !important;
}

.subscription-warning.info .btn-light:hover {
  background: #2563eb !important;
  border-color: #2563eb !important;
  color: #fff !important;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 768px) {
  .subscription-warning {
    margin-left: 0 !important;
    margin-top: 0.5rem !important;
    width: 100% !important;
    max-width: 100% !important;
    flex-direction: column !important;
    align-items: flex-start !important;
  }

  .subscription-warning span {
    margin-bottom: 0.5rem !important;
    margin-right: 0 !important;
  }

  .subscription-warning .btn {
    width: 100% !important;
    margin-left: 0 !important;
  }
}
</style>
